import type { ProposedOrderTrackingInvoiceBillingInput } from "@redotech/admin-sdk/rpc/schema/order-tracking/order-tracking-billing-period";
import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { AnimatedSkeletonBlock } from "@redotech/arbiter/skeleton";
import { Text } from "@redotech/arbiter/text";
import { memo } from "react";
import type { OrderTrackingBillingValue } from "./order-tracking-billing";

export const ApplyOrderTrackingBillingToCurrentPeriodModal = memo(
  function ApplyOrderTrackingBillingToCurrentPeriodModal({
    open,
    onClose,
    onConfirm,
    loading,
    previewError,
    previewLoading,
    usageChargeUnderCurrentConfigUsd,
    usageChargeUnderProposedConfigUsd,
  }: {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
    previewError: boolean;
    previewLoading: boolean;
    usageChargeUnderCurrentConfigUsd: number | undefined;
    usageChargeUnderProposedConfigUsd: number | undefined;
  }) {
    return (
      <RedoModal
        isOpen={open}
        modalSize="md"
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Apply to current period",
          onClick: onConfirm,
          pending: loading,
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose }}
        title="Apply settings to current period"
      >
        <Flex dir="column" gap="lg" p="lg">
          <Text fontSize="sm">
            This updates the current billing period to use the settings shown in
            this form. The merchant may be surprised if their invoice changes
            mid-period unexpectedly.
          </Text>
          {previewError ? (
            <Text fontSize="sm" textColor="error">
              We encountered an error estimating the current usage.
            </Text>
          ) : (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="semibold">
                Current period usage charge (estimate)
              </Text>
              <UsageChargeEstimateRow
                label="Under current configuration:"
                loading={previewLoading}
                usageChargeUsd={usageChargeUnderCurrentConfigUsd}
              />
              <UsageChargeEstimateRow
                label="Under new configuration:"
                loading={previewLoading}
                usageChargeUsd={usageChargeUnderProposedConfigUsd}
              />
            </Flex>
          )}
        </Flex>
      </RedoModal>
    );
  },
);

const UsageChargeEstimateRow = memo(function UsageChargeEstimateRow({
  label,
  loading,
  usageChargeUsd,
}: {
  label: string;
  loading: boolean;
  usageChargeUsd: number | undefined;
}) {
  return (
    <Flex align="center" gap="sm">
      <Text fontSize="sm">{label}</Text>
      {loading ? (
        <AnimatedSkeletonBlock height="18px" width="56px" />
      ) : (
        <Text fontSize="sm">${usageChargeUsd?.toFixed(2) ?? "—"}</Text>
      )}
    </Flex>
  );
});

export function proposedBillingFromForm(
  invoiceBilling: OrderTrackingBillingValue["invoiceBilling"],
): ProposedOrderTrackingInvoiceBillingInput {
  return {
    perOrderSettings: invoiceBilling.perOrderSettings,
    perShipmentSettings: invoiceBilling.perShipmentSettings,
    perSmsSettings: invoiceBilling.perSmsSettings,
    perEmailSettings: invoiceBilling.perEmailSettings,
  };
}
